import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Container, Card, CardContent, CardActionArea,
  Switch, FormControlLabel, Alert, CircularProgress, Button,
  TextField, IconButton, InputAdornment, Snackbar, Collapse,
  Chip, Stack, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
  Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon, Error as ErrorIcon,
  SmartToy as BotIcon, Security as SecurityIcon, Notifications as NotifyIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import ChangePassword from '../components/auth/ChangePassword';
import PasskeyManager from '../components/auth/PasskeyManager';
import TotpManager from '../components/auth/TotpManager';
import { apiRequest } from '../utils/api';

const SecuritySection = () => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card sx={{ mb: 3 }}>
      <CardActionArea onClick={() => setExpanded(!expanded)} sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon color="primary" />
          <Typography variant="h6" component="h2">Security</Typography>
        </Box>
        <IconButton size="small" disableRipple>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </CardActionArea>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent sx={{ p: 3, pt: 0 }}>
          <Divider sx={{ mb: 2 }} />
          <ChangePassword />
          <PasskeyManager />
          <TotpManager />
        </CardContent>
      </Collapse>
    </Card>
  );
};

const BotConfigSection = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [snackbar, setSnackbar] = useState(null);
  const [showDiscordToken, setShowDiscordToken] = useState(false);
  const [showSession, setShowSession] = useState(false);
  const [discordToken, setDiscordToken] = useState('');
  const [tgForm, setTgForm] = useState({ apiId: '', apiHash: '', sessionString: '', listenerChatIds: '', listenerWebhookToken: '' });
  const [plaidForm, setPlaidForm] = useState({ clientId: '', secret: '', env: 'sandbox' });
  const [showPlaidSecret, setShowPlaidSecret] = useState(false);
  const [showDiscordHelp, setShowDiscordHelp] = useState(false);
  const [showTelegramHelp, setShowTelegramHelp] = useState(false);
  // Telegram session refresh dialog
  const [tgSessionDlg, setTgSessionDlg] = useState({ open: false, step: 'phone', phone: '', code: '', password: '', busy: false });

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiRequest('/api/auth/bot-config');
      setConfig(resp.data);
      setDiscordToken(resp.data?.discord?.configured ? '(configured)' : '');
      setTgForm({
        apiId: resp.data?.telegram?.apiId || '',
        apiHash: resp.data?.telegram?.apiHash || '',
        sessionString: resp.data?.telegram?.sessionString || '',
        listenerChatIds: resp.data?.telegram?.listenerChatIds || '',
        listenerWebhookToken: resp.data?.telegram?.listenerWebhookToken || '',
      });
      setPlaidForm({
        clientId: resp.data?.plaid?.clientId || '',
        secret: '',
        env: resp.data?.plaid?.env || 'sandbox',
      });
    } catch (e) {
      setSnackbar({ severity: 'error', text: e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSaveDiscord = async () => {
    setSaving(true);
    try {
      const tokenToSend = discordToken === '(configured)' ? '' : discordToken;
      await apiRequest('/api/auth/discord-bot-token', { method: 'PUT', body: JSON.stringify({ token: tokenToSend }) });
      setSnackbar({ severity: 'success', text: 'Discord bot token saved' });
      fetchConfig();
    } catch (e) { setSnackbar({ severity: 'error', text: e.message }); } finally { setSaving(false); }
  };

  const handleSaveTelegram = async () => {
    setSaving(true);
    try {
      await apiRequest('/api/auth/telegram-config', { method: 'PUT', body: JSON.stringify(tgForm) });
      setSnackbar({ severity: 'success', text: 'Telegram config saved' });
      fetchConfig();
    } catch (e) { setSnackbar({ severity: 'error', text: e.message }); } finally { setSaving(false); }
  };

  const handleSavePlaid = async () => {
    setSaving(true);
    try {
      const body = { clientId: plaidForm.clientId, env: plaidForm.env };
      if (plaidForm.secret) body.secret = plaidForm.secret; // empty = keep existing
      await apiRequest('/api/auth/plaid-config', { method: 'PUT', body: JSON.stringify(body) });
      setSnackbar({ severity: 'success', text: 'Plaid config saved' });
      setPlaidForm(f => ({ ...f, secret: '' }));
      fetchConfig();
    } catch (e) { setSnackbar({ severity: 'error', text: e.message }); } finally { setSaving(false); }
  };

  const handleBotToggle = async (key, enabled) => {
    // Optimistic update
    setConfig(prev => ({
      ...prev,
      [key === 'discordBotEnabled' ? 'discord' : 'telegram']: {
        ...prev?.[key === 'discordBotEnabled' ? 'discord' : 'telegram'],
        enabled,
      },
    }));
    try {
      await apiRequest('/api/auth/bot-toggles', { method: 'PUT', body: JSON.stringify({ [key]: enabled }) });
      setSnackbar({ severity: 'success', text: `${key === 'discordBotEnabled' ? 'Discord bot' : 'Telegram listener'} ${enabled ? 'enabled' : 'disabled'}` });
    } catch (e) {
      setSnackbar({ severity: 'error', text: e.message });
      fetchConfig(); // revert on failure
    }
  };

  const openSessionDialog = () => setTgSessionDlg({ open: true, step: 'phone', phone: '', code: '', password: '', busy: false });
  const closeSessionDialog = () => setTgSessionDlg(d => ({ ...d, open: false, busy: false }));

  const handleSessionStart = async () => {
    setTgSessionDlg(d => ({ ...d, busy: true }));
    try {
      const resp = await apiRequest('/api/auth/telegram-session/start', {
        method: 'POST', body: JSON.stringify({ phoneNumber: tgSessionDlg.phone }),
      });
      if (resp.data?.done) {
        setSnackbar({ severity: 'success', text: 'Signed in — session saved' });
        closeSessionDialog();
        fetchConfig();
      } else {
        setTgSessionDlg(d => ({ ...d, step: 'code', busy: false }));
      }
    } catch (e) {
      setSnackbar({ severity: 'error', text: e.message });
      setTgSessionDlg(d => ({ ...d, busy: false }));
    }
  };

  const handleSessionVerify = async () => {
    setTgSessionDlg(d => ({ ...d, busy: true }));
    try {
      const body = tgSessionDlg.step === 'password' ? { password: tgSessionDlg.password } : { code: tgSessionDlg.code };
      const resp = await apiRequest('/api/auth/telegram-session/verify', { method: 'POST', body: JSON.stringify(body) });
      if (resp.data?.needsPassword) {
        setTgSessionDlg(d => ({ ...d, step: 'password', busy: false }));
      } else if (resp.data?.success) {
        setSnackbar({ severity: 'success', text: 'Telegram session refreshed and saved' });
        closeSessionDialog();
        fetchConfig();
      }
    } catch (e) {
      setSnackbar({ severity: 'error', text: e.message });
      setTgSessionDlg(d => ({ ...d, busy: false }));
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardActionArea onClick={() => setExpanded(!expanded)} sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BotIcon color="primary" />
          <Typography variant="h6" component="h2">Bot Configuration</Typography>
          {config && (
            <Stack direction="row" spacing={0.5} sx={{ ml: 1 }}>
              {config.discord?.configured && <Chip size="small" color="success" label="Discord" />}
              {config.telegram?.configured && <Chip size="small" color="success" label="Telegram" />}
              {config.plaid?.configured && <Chip size="small" color="success" label="Plaid" />}
            </Stack>
          )}
        </Box>
        <IconButton size="small" disableRipple>{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </CardActionArea>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent sx={{ p: 3, pt: 0 }}>
          <Divider sx={{ mb: 2 }} />
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
          ) : (
            <>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">Discord Bot</Typography>
                  {config?.discord?.configured ? (
                    <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Configured" />
                  ) : (
                    <Chip size="small" color="default" icon={<ErrorIcon />} label="Not configured" />
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={config?.discord?.enabled !== false}
                        onChange={(e) => handleBotToggle('discordBotEnabled', e.target.checked)}
                      />
                    }
                    label={config?.discord?.enabled !== false ? 'Enabled' : 'Disabled'}
                    sx={{ ml: 'auto' }}
                  />
                </Box>
                <Button size="small" onClick={() => setShowDiscordHelp(v => !v)} endIcon={showDiscordHelp ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ mb: 1 }}>
                  How to get a bot token
                </Button>
                <Collapse in={showDiscordHelp}>
                  <Alert severity="info" sx={{ mb: 2, fontSize: '0.85rem' }}>
                    <ol style={{ margin: 0, paddingLeft: 18 }}>
                      <li>Go to <b>discord.com/developers/applications</b> → <b>New Application</b></li>
                      <li>Open the app → <b>Bot</b> (left sidebar) → <b>Reset Token</b> → copy the token</li>
                      <li>On the same Bot page enable <b>Message Content Intent</b> (and Server Members Intent if you want member events)</li>
                      <li><b>OAuth2 → URL Generator</b>: scope <code>bot</code>, permissions: <i>Send Messages, Read Message History, View Channels</i> → open the generated URL to invite it to your server</li>
                      <li>Paste the token above. Channel IDs for alerts come from Discord: right-click a channel (Developer Mode on) → Copy Channel ID</li>
                    </ol>
                  </Alert>
                </Collapse>
                <TextField fullWidth size="small" type={showDiscordToken ? 'text' : 'password'} label="Discord Bot Token"
                  value={discordToken} onChange={(e) => setDiscordToken(e.target.value)} placeholder="Enter Discord bot token" sx={{ mb: 1 }}
                  InputProps={{ endAdornment: (<InputAdornment position="end"><IconButton size="small" onClick={() => setShowDiscordToken(!showDiscordToken)} edge="end">{showDiscordToken ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}</IconButton></InputAdornment>) }}
                />
                <Button variant="contained" size="small" onClick={handleSaveDiscord} disabled={saving} startIcon={saving ? <CircularProgress size={14} /> : null}>Save Discord</Button>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">Telegram Listener</Typography>
                  {config?.telegram?.configured ? (
                    <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Configured" />
                  ) : (
                    <Chip size="small" color="default" icon={<ErrorIcon />} label="Not configured" />
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={config?.telegram?.enabled !== false}
                        onChange={(e) => handleBotToggle('telegramListenerEnabled', e.target.checked)}
                      />
                    }
                    label={config?.telegram?.enabled !== false ? 'Enabled' : 'Disabled'}
                    sx={{ ml: 'auto' }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Used for listening to trade alerts in Telegram groups.
                </Typography>
                <Button size="small" onClick={() => setShowTelegramHelp(v => !v)} endIcon={showTelegramHelp ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ mb: 1.5 }}>
                  How to get API ID / Hash / Session
                </Button>
                <Collapse in={showTelegramHelp}>
                  <Alert severity="info" sx={{ mb: 2, fontSize: '0.85rem' }}>
                    <b>API ID + API Hash</b>
                    <ol style={{ margin: '4px 0 8px', paddingLeft: 18 }}>
                      <li>Go to <b>my.telegram.org</b> → log in with your phone number</li>
                      <li><b>API development tools</b> → <b>Create application</b> (any name/short name works)</li>
                      <li>Copy <code>api_id</code> (a number) and <code>api_hash</code> (a hex string)</li>
                    </ol>
                    <b>Session String</b>
                    <ol style={{ margin: '4px 0 8px', paddingLeft: 18 }}>
                      <li>Save API ID/Hash first, then click <b>Refresh Session String</b> below — enter your phone number, the code Telegram sends you, and your 2FA password if enabled. It signs in and stores the session.</li>
                    </ol>
                    <b>Listener Chat IDs</b>
                    <ol style={{ margin: '4px 0', paddingLeft: 18 }}>
                      <li>The numeric ID of each group/channel to listen on. Open <b>web.telegram.org</b>, click the group — the URL ends with <code>#-1234567890</code></li>
                      <li>For supergroups/channels prefix with <code>-100</code>: URL <code>#-987654321</code> becomes <code>-100987654321</code></li>
                      <li>Or forward a group message to <b>@userinfobot</b> to get the chat id</li>
                    </ol>
                  </Alert>
                </Collapse>
                <Stack spacing={1.5}>
                  <TextField fullWidth size="small" label="Telegram API ID" value={tgForm.apiId} onChange={(e) => setTgForm({ ...tgForm, apiId: e.target.value })} placeholder="e.g. 12345678" />
                  <TextField fullWidth size="small" label="Telegram API Hash" value={tgForm.apiHash} onChange={(e) => setTgForm({ ...tgForm, apiHash: e.target.value })} placeholder="e.g. abc123def456..." />
                  <TextField fullWidth size="small" type={showSession ? 'text' : 'password'} label="Telegram Session String" value={tgForm.sessionString} onChange={(e) => setTgForm({ ...tgForm, sessionString: e.target.value })} placeholder="Session string from saveTelegramSession script"
                    InputProps={{ endAdornment: (<InputAdornment position="end"><IconButton size="small" onClick={() => setShowSession(!showSession)} edge="end">{showSession ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}</IconButton></InputAdornment>) }}
                  />
                  <Button variant="outlined" size="small" onClick={openSessionDialog} sx={{ alignSelf: 'flex-start' }}>
                    Refresh Session String
                  </Button>
                  <TextField fullWidth size="small" label="Listener Chat IDs (comma-separated)" value={tgForm.listenerChatIds} onChange={(e) => setTgForm({ ...tgForm, listenerChatIds: e.target.value })} placeholder="e.g. -1001234567890,-1009876543210" />
                  <TextField fullWidth size="small" label="Listener Webhook Token" value={tgForm.listenerWebhookToken} onChange={(e) => setTgForm({ ...tgForm, listenerWebhookToken: e.target.value })} placeholder="Webhook auth token for forwarding messages" />
                </Stack>
                <Button variant="contained" size="small" onClick={handleSaveTelegram} disabled={saving} startIcon={saving ? <CircularProgress size={14} /> : null} sx={{ mt: 1.5 }}>Save Telegram</Button>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">Plaid (Wealthfront / Betterment)</Typography>
                  {config?.plaid?.configured ? (
                    <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Configured" />
                  ) : (
                    <Chip size="small" color="default" icon={<ErrorIcon />} label="Not configured" />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Credentials from your Plaid dashboard (Team Settings → Keys). Used to link brokerage accounts that have no direct API.
                </Typography>
                <Stack spacing={1.5}>
                  <TextField fullWidth size="small" label="Plaid Client ID" value={plaidForm.clientId} onChange={(e) => setPlaidForm({ ...plaidForm, clientId: e.target.value })} placeholder="e.g. 64abc123..." />
                  <TextField fullWidth size="small" type={showPlaidSecret ? 'text' : 'password'} label="Plaid Secret" value={plaidForm.secret}
                    onChange={(e) => setPlaidForm({ ...plaidForm, secret: e.target.value })}
                    placeholder={config?.plaid?.configured ? '(saved — enter to replace)' : 'Plaid secret'}
                    InputProps={{ endAdornment: (<InputAdornment position="end"><IconButton size="small" onClick={() => setShowPlaidSecret(!showPlaidSecret)} edge="end">{showPlaidSecret ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}</IconButton></InputAdornment>) }}
                  />
                  <TextField select fullWidth size="small" label="Plaid Environment" value={plaidForm.env} onChange={(e) => setPlaidForm({ ...plaidForm, env: e.target.value })}
                    SelectProps={{ native: true }}
                  >
                    <option value="sandbox">Sandbox (test institutions)</option>
                    <option value="production">Production / Limited Production (real accounts)</option>
                  </TextField>
                </Stack>
                <Button variant="contained" size="small" onClick={handleSavePlaid} disabled={saving} startIcon={saving ? <CircularProgress size={14} /> : null} sx={{ mt: 1.5 }}>Save Plaid</Button>
              </Box>
            </>
          )}
        </CardContent>
      </Collapse>
      <Dialog open={tgSessionDlg.open} onClose={tgSessionDlg.busy ? undefined : closeSessionDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Refresh Telegram Session</DialogTitle>
        <DialogContent>
          {tgSessionDlg.step === 'phone' && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the phone number linked to your Telegram account. A login code will be sent to your Telegram app.
              </Typography>
              <TextField autoFocus fullWidth size="small" label="Phone Number" value={tgSessionDlg.phone}
                onChange={(e) => setTgSessionDlg(d => ({ ...d, phone: e.target.value }))}
                placeholder="+1234567890" disabled={tgSessionDlg.busy} />
            </>
          )}
          {tgSessionDlg.step === 'code' && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the login code you received in Telegram.
              </Typography>
              <TextField autoFocus fullWidth size="small" label="Login Code" value={tgSessionDlg.code}
                onChange={(e) => setTgSessionDlg(d => ({ ...d, code: e.target.value }))}
                placeholder="e.g. 12345" disabled={tgSessionDlg.busy} />
            </>
          )}
          {tgSessionDlg.step === 'password' && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your account has two-step verification enabled. Enter your Telegram password.
              </Typography>
              <TextField autoFocus fullWidth size="small" type="password" label="Telegram Password" value={tgSessionDlg.password}
                onChange={(e) => setTgSessionDlg(d => ({ ...d, password: e.target.value }))}
                disabled={tgSessionDlg.busy} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSessionDialog} disabled={tgSessionDlg.busy}>Cancel</Button>
          {tgSessionDlg.step === 'phone' ? (
            <Button variant="contained" onClick={handleSessionStart} disabled={tgSessionDlg.busy || !tgSessionDlg.phone.trim()}
              startIcon={tgSessionDlg.busy ? <CircularProgress size={14} /> : null}>Send Code</Button>
          ) : (
            <Button variant="contained" onClick={handleSessionVerify}
              disabled={tgSessionDlg.busy || (tgSessionDlg.step === 'code' ? !tgSessionDlg.code.trim() : !tgSessionDlg.password)}
              startIcon={tgSessionDlg.busy ? <CircularProgress size={14} /> : null}>
              {tgSessionDlg.step === 'password' ? 'Submit Password' : 'Verify & Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Snackbar open={!!snackbar} autoHideDuration={6000} onClose={() => setSnackbar(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar(null)} severity={snackbar?.severity || 'info'}>{snackbar?.text}</Alert>
      </Snackbar>
    </Card>
  );
};

const NotificationConfigSection = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [snackbar, setSnackbar] = useState(null);
  const [showTgToken, setShowTgToken] = useState(false);
  const [testing, setTesting] = useState(null);

  // Telegram form
  const [tgEnabled, setTgEnabled] = useState(false);
  const [tgBotToken, setTgBotToken] = useState('');
  const [tgChatId, setTgChatId] = useState('');
  const [tgExecChatId, setTgExecChatId] = useState('');
  const [tgConfig, setTgConfig] = useState({});
  const [tgSettingId, setTgSettingId] = useState(null);

  // Email form
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSettingId, setEmailSettingId] = useState(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiRequest('/api/notifications/settings');
      const list = resp.data?.settings || [];
      setSettings(list);
      const tg = list.find(s => s.provider === 'telegram');
      if (tg) {
        setTgEnabled(tg.is_enabled !== false);
        setTgBotToken(tg.config?.bot_token || '');
        setTgChatId(tg.config?.chat_id || '');
        setTgExecChatId(tg.config?.execution_chat_id || '');
        setTgConfig(tg.config || {});
        setTgSettingId(tg.id);
      }
      const em = list.find(s => s.provider === 'email');
      if (em) {
        setEmailEnabled(em.is_enabled !== false);
        setEmailTo(em.config?.to_email || '');
        setEmailSettingId(em.id);
      }
    } catch (e) {
      setSnackbar({ severity: 'error', text: e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSaveTelegram = async () => {
    setSaving(true);
    try {
      await apiRequest('/api/notifications/settings', {
        method: 'POST',
        body: JSON.stringify({
          id: tgSettingId,
          provider: 'telegram',
          isEnabled: tgEnabled,
          config: { ...tgConfig, bot_token: tgBotToken, chat_id: tgChatId, execution_chat_id: tgExecChatId },
          label: 'Telegram',
        }),
      });
      setSnackbar({ severity: 'success', text: 'Telegram notification saved' });
      fetchSettings();
    } catch (e) { setSnackbar({ severity: 'error', text: e.message }); } finally { setSaving(false); }
  };

  const handleSaveEmail = async () => {
    setSaving(true);
    try {
      await apiRequest('/api/notifications/settings', {
        method: 'POST',
        body: JSON.stringify({
          id: emailSettingId,
          provider: 'email',
          isEnabled: emailEnabled,
          config: { to_email: emailTo },
          label: 'Email',
        }),
      });
      setSnackbar({ severity: 'success', text: 'Email notification saved' });
      fetchSettings();
    } catch (e) { setSnackbar({ severity: 'error', text: e.message }); } finally { setSaving(false); }
  };

  const handleTest = async (provider) => {
    setTesting(provider);
    try {
      await apiRequest('/api/notifications/test', { method: 'POST', body: JSON.stringify({ provider }) });
      setSnackbar({ severity: 'success', text: `Test ${provider} notification sent` });
    } catch (e) { setSnackbar({ severity: 'error', text: e.message }); } finally { setTesting(null); }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardActionArea onClick={() => setExpanded(!expanded)} sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotifyIcon color="primary" />
          <Typography variant="h6" component="h2">Notification Configuration</Typography>
          {settings.filter(s => s.is_enabled !== false).length > 0 && (
            <Chip size="small" color="success" label={`${settings.filter(s => s.is_enabled !== false).length} active`} />
          )}
        </Box>
        <IconButton size="small" disableRipple>{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </CardActionArea>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent sx={{ p: 3, pt: 0 }}>
          <Divider sx={{ mb: 2 }} />
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
          ) : (
            <>
              {/* Telegram Notification */}
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={<Switch checked={tgEnabled} onChange={(e) => setTgEnabled(e.target.checked)} />}
                  label={<Typography variant="subtitle1" fontWeight="bold">Telegram</Typography>}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 4 }}>
                  Receive alert notifications via Telegram bot.
                </Typography>
                <Stack spacing={1.5} sx={{ ml: 4 }}>
                  <TextField fullWidth size="small" type={showTgToken ? 'text' : 'password'} label="Telegram Bot Token" value={tgBotToken} onChange={(e) => setTgBotToken(e.target.value)} placeholder="Bot token from @BotFather"
                    InputProps={{ endAdornment: (<InputAdornment position="end"><IconButton size="small" onClick={() => setShowTgToken(!showTgToken)} edge="end">{showTgToken ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}</IconButton></InputAdornment>) }}
                  />
                  <TextField fullWidth size="small" label="Telegram Chat ID" value={tgChatId} onChange={(e) => setTgChatId(e.target.value)} placeholder="e.g. -1001234567890 or 123456789" helperText="Trade alerts and general notifications. Comma-separate for multiple chats." />
                  <TextField fullWidth size="small" label="Executions Chat ID (optional)" value={tgExecChatId} onChange={(e) => setTgExecChatId(e.target.value)} placeholder="e.g. -1001234567890" helperText="Order submitted / filled / canceled notifications go here instead. Leave blank to use the Chat ID above." />
                </Stack>
                <Button variant="contained" size="small" onClick={handleSaveTelegram} disabled={saving} startIcon={saving ? <CircularProgress size={14} /> : null} sx={{ mt: 1.5, ml: 4 }}>Save Telegram</Button>
                <Button variant="outlined" size="small" onClick={() => handleTest('telegram')} disabled={testing === 'telegram'} startIcon={testing === 'telegram' ? <CircularProgress size={14} /> : null} sx={{ mt: 1.5, ml: 1 }}>Test Telegram</Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Email Notification */}
              <Box>
                <FormControlLabel
                  control={<Switch checked={emailEnabled} onChange={(e) => setEmailEnabled(e.target.checked)} />}
                  label={<Typography variant="subtitle1" fontWeight="bold">Email</Typography>}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 4 }}>
                  Receive alert notifications via email.
                </Typography>
                <Box sx={{ ml: 4 }}>
                  <TextField fullWidth size="small" label="Email Address" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="you@example.com" sx={{ mb: 1 }} />
                </Box>
                <Button variant="contained" size="small" onClick={handleSaveEmail} disabled={saving} startIcon={saving ? <CircularProgress size={14} /> : null} sx={{ mt: 1, ml: 4 }}>Save Email</Button>
                <Button variant="outlined" size="small" onClick={() => handleTest('email')} disabled={testing === 'email'} startIcon={testing === 'email' ? <CircularProgress size={14} /> : null} sx={{ mt: 1, ml: 1 }}>Test Email</Button>
              </Box>
            </>
          )}
        </CardContent>
      </Collapse>
      <Snackbar open={!!snackbar} autoHideDuration={6000} onClose={() => setSnackbar(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar(null)} severity={snackbar?.severity || 'info'}>{snackbar?.text}</Alert>
      </Snackbar>
    </Card>
  );
};

const MarketDataSection = () => {
  const [expanded, setExpanded] = useState(false);
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState('auto');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await apiRequest('/api/market-quotes/stream-options');
        setOptions(resp.data?.options || []);
        setSelected(resp.data?.selected || 'auto');
      } catch (e) {
        console.error('Failed to load stream options', e);
      }
      setLoading(false);
    };
    load();
  }, []);

  const handleChange = async (e) => {
    const value = e.target.value;
    setSelected(value);
    setSaving(true);
    try {
      await apiRequest('/api/market-quotes/stream-preference', {
        method: 'PUT',
        body: JSON.stringify({ broker: value }),
      });
      setSnackbar({ severity: 'success', text: 'Quote source saved' });
    } catch (e) {
      setSnackbar({ severity: 'error', text: 'Failed to save quote source' });
    }
    setSaving(false);
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardActionArea onClick={() => setExpanded(!expanded)} sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SpeedIcon color="primary" />
          <Typography variant="h6" component="h2">Market Data</Typography>
        </Box>
        <IconButton size="small" disableRipple>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </CardActionArea>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent sx={{ p: 3, pt: 0 }}>
          <Divider sx={{ mb: 2 }} />
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Source for live quotes in the toolbar ticker and watchlist. Streaming sources update in
                real time including extended hours; REST polling may be delayed or frozen outside market hours.
              </Typography>
              <FormControl size="small" sx={{ minWidth: 320 }}>
                <InputLabel>Quote Source</InputLabel>
                <Select
                  value={selected}
                  label="Quote Source"
                  onChange={handleChange}
                  disabled={saving}
                >
                  {options.map(opt => (
                    <MenuItem key={opt.id} value={opt.id} disabled={!opt.available}>
                      {opt.label}{!opt.available ? ' (no account configured)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {saving && <CircularProgress size={16} sx={{ ml: 2 }} />}
            </>
          )}
        </CardContent>
      </Collapse>
      <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar(null)} severity={snackbar?.severity || 'info'}>{snackbar?.text}</Alert>
      </Snackbar>
    </Card>
  );
};

const UserProfile = () => {
  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 1, sm: 4 }, mb: 4, px: { xs: 1, sm: 3 } }}>
      <SecuritySection />
      <MarketDataSection />
      <BotConfigSection />
      <NotificationConfigSection />
    </Container>
  );
};

export default UserProfile;
